/**
 * Debug & Production Logger Utility
 * 
 * debugLog/debugWarn/debugInfo — only in development (no sensitive data leak)
 * prodLog/prodWarn/prodError     — always log (safe for production runtime logs)
 * 
 * In Digital Ocean (or any Node host), server-side console output appears in
 * the Runtime Logs panel. Client-side output only appears in browser devtools.
 */

// ─── Environment helpers ────────────────────────────────────────────
export const isDevelopment = () => process.env.NEXT_PUBLIC_ENV === 'development';
export const isProduction = () => process.env.NEXT_PUBLIC_ENV === 'production';

// ─── Development-only loggers (unchanged) ───────────────────────────
export const debugLog = (...args) => { if (isDevelopment()) console.log(...args); };
export const debugWarn = (...args) => { if (isDevelopment()) console.warn(...args); };
export const debugInfo = (...args) => { if (isDevelopment()) console.info(...args); };

// ─── Production-safe loggers ────────────────────────────────────────
// These ALWAYS log, so only use them for information that is:
//   • NOT sensitive (no tokens, passwords, PII)
//   • Operationally useful (errors, auth flow events, performance)

/**
 * Production-safe info log. Shows in DO runtime logs (server-side) and
 * browser console (client-side). Prefixed with [DASHBOARD] for easy grep.
 */
export const prodLog = (...args) => {
  console.log('[DASHBOARD]', ...args);
};

/**
 * Production-safe warning. Use for non-critical issues that need attention.
 */
export const prodWarn = (...args) => {
  console.warn('[DASHBOARD WARN]', ...args);
};

/**
 * Production-safe error log. Use for all caught errors and failures.
 * These will appear in DO runtime logs and can be searched/alerted on.
 */
export const prodError = (...args) => {
  console.error('[DASHBOARD ERROR]', ...args);
};

export default {
  // Dev-only
  log: debugLog,
  warn: debugWarn,
  info: debugInfo,
  isDevelopment,
  // Production-safe
  prodLog,
  prodWarn,
  prodError,
  isProduction,
};
